import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  FileText,
  Target,
  BarChart3,
  RefreshCw,
  Filter,
  Sparkles,
  Calendar
} from 'lucide-react';
import AIAssistantModal from '../components/AIAssistantModal';
import DeadlineWidget from '../components/DeadlineWidget';

const ComplianceConsole = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [kpiMappings, setKpiMappings] = useState(null);
  const [activeTab, setActiveTab] = useState('gaps');
  const [selectedFramework, setSelectedFramework] = useState('all');
  
  // AI Disclosure Draft State
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [disclosureLoading, setDisclosureLoading] = useState(false);
  const [disclosureResult, setDisclosureResult] = useState(null);
  const [disclosureError, setDisclosureError] = useState(null);
  const [selectedGap, setSelectedGap] = useState(null);

  useEffect(() => {
    fetchData();
    logAccess();
  }, []);

  const logAccess = async () => {
    // Silently ignore audit logging errors (apiFetch never throws for HTTP errors)
    await apiFetch(`${API}/audit/log-access`, {
      method: 'POST',
      body: {
        dashboard_id: 'compliance',
        dashboard_name: 'Compliance Console',
        action: 'dashboard_view',
        timestamp: new Date().toISOString()
      }
    });
  };

  const fetchData = async () => {
    const [gapData, kpiData] = await Promise.all([
      apiFetch(`${API}/compliance/analyze`),
      apiFetch(`${API}/regulations/kpi-mappings`)
    ]);

    if (gapData?._error || kpiData?._error) {
      console.error('Error fetching data:', gapData?.message || kpiData?.message);
      setLoading(false);
      return;
    }

    setGapAnalysis(gapData);
    setKpiMappings(kpiData);
    setLoading(false);
  };

  const frameworks = [
    { id: 'esrs', name: 'ESRS', score: 45, total: 82, covered: 37, color: 'emerald' },
    { id: 'sfdr', name: 'SFDR PAI', score: 60, total: 18, covered: 11, color: 'blue' },
    { id: 'tcfd', name: 'TCFD', score: 75, total: 11, covered: 8, color: 'purple' },
    { id: 'gri', name: 'GRI', score: 55, total: 36, covered: 20, color: 'amber' }
  ];

  const gaps = [
    { id: 'E1-6', standard: 'ESRS E1', name: 'Scope 3 GHG Emissions', priority: 'critical', status: 'not_started', dueDate: '2025-03-31' },
    { id: 'S1-1', standard: 'ESRS S1', name: 'Workforce Policies', priority: 'high', status: 'in_progress', dueDate: '2025-04-15' },
    { id: 'G1-1', standard: 'ESRS G1', name: 'Business Conduct Policies', priority: 'high', status: 'not_started', dueDate: '2025-04-30' },
    { id: 'PAI-1', standard: 'SFDR', name: 'GHG Emissions Intensity', priority: 'medium', status: 'in_progress', dueDate: '2025-06-30' },
    { id: 'E2-4', standard: 'ESRS E2', name: 'Pollution of Water', priority: 'low', status: 'not_started', dueDate: '2025-06-30' }
  ];

  const priorityColors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-amber-100 text-amber-700 border-amber-200',
    medium: 'bg-blue-100 text-blue-700 border-blue-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const statusColors = {
    not_started: 'text-red-600',
    in_progress: 'text-amber-600',
    complete: 'text-emerald-600'
  };

  // Generate Disclosure Draft with AI
  const generateDisclosureDraft = async (gap) => {
    setSelectedGap(gap);
    setShowDisclosureModal(true);
    setDisclosureLoading(true);
    setDisclosureError(null);
    setDisclosureResult(null);
    
    const data = await apiFetch(`${API}/ai/compliance/draft-disclosure`, {
      method: 'POST',
      body: {
        gap_id: gap.id,
        standard: gap.standard,
        requirement_name: gap.name,
        available_data: null
      }
    });
    if (data?._error) {
      setDisclosureError(data.message);
    } else {
      setDisclosureResult(data);
    }
    setDisclosureLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center animate-pulse">
          <Shield size={24} className="text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-console">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard-selector')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Compliance Console
            </h1>
            <p className="text-slate-600">Regulatory tracking and gap analysis for compliance managers</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw size={16} />
          Refresh Analysis
        </button>
      </div>

      {/* Framework Overview Cards */}
      <div className="grid grid-cols-4 gap-4">
        {frameworks.map((fw) => (
          <div
            key={fw.id}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedFramework === fw.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            onClick={() => setSelectedFramework(selectedFramework === fw.id ? 'all' : fw.id)}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-900">{fw.name}</span>
              <span className={`text-lg font-bold ${fw.score >= 70 ? 'text-emerald-600' : fw.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                {fw.score}%
              </span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${fw.score >= 70 ? 'bg-emerald-500' : fw.score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${fw.score}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">{fw.covered}/{fw.total} datapoints</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'gaps', label: 'Gap Analysis', icon: AlertTriangle },
          { id: 'kpis', label: 'KPI Ownership', icon: Target },
          { id: 'remediation', label: 'Remediation Tracker', icon: CheckCircle },
          { id: 'alerts', label: 'Regulatory Alerts', icon: FileText }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Gap Analysis Tab */}
      {activeTab === 'gaps' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Compliance Gaps ({gaps.length})</h3>
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-400" />
              <select className="text-sm border border-slate-200 rounded-lg px-3 py-1.5">
                <option>All Priorities</option>
                <option>Critical Only</option>
                <option>High Priority</option>
              </select>
            </div>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Datapoint</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Standard</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Requirement</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Due Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((gap) => (
                <tr key={gap.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-sm text-slate-900">{gap.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{gap.standard}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{gap.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${priorityColors[gap.priority]}`}>
                      {gap.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${statusColors[gap.status]}`}>
                      {gap.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{gap.dueDate}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => generateDisclosureDraft(gap)}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1"
                        data-testid={`draft-disclosure-${gap.id}`}
                      >
                        <Sparkles size={14} /> Draft
                      </button>
                      <Link
                        to="/data-upload"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                      >
                        Resolve <ChevronRight size={14} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* KPI Ownership Tab */}
      {activeTab === 'kpis' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4">KPI Ownership Matrix</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(kpiMappings?.mappings || {}).slice(0, 6).map(([kpiId, kpi]) => (
              <div key={kpiId} className="p-4 border border-slate-200 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">{kpi.name}</h4>
                <p className="text-xs text-slate-500 mb-3">{kpi.description?.slice(0, 80)}...</p>
                <div className="flex flex-wrap gap-1">
                  {kpi.regulations?.map((reg, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      {reg}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remediation Tab */}
      {activeTab === 'remediation' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Remediation Progress</h3>
          <div className="space-y-4">
            {gaps.map((gap) => (
              <div key={gap.id} className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900">{gap.id}: {gap.name}</span>
                  <span className={`text-sm ${statusColors[gap.status]}`}>
                    {gap.status === 'complete' ? '100%' : gap.status === 'in_progress' ? '50%' : '0%'}
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      gap.status === 'complete' ? 'bg-emerald-500' : gap.status === 'in_progress' ? 'bg-amber-500' : 'bg-slate-300'
                    }`}
                    style={{ width: gap.status === 'complete' ? '100%' : gap.status === 'in_progress' ? '50%' : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Regulatory Change Alerts</h3>
            <div className="space-y-3">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-900">ESRS Set 1 Update - New Guidance</p>
                  <p className="text-sm text-amber-700 mt-1">EFRAG released updated implementation guidance for E1-6 GHG emissions disclosure.</p>
                  <p className="text-xs text-amber-600 mt-2">2 days ago</p>
                </div>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <FileText className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-blue-900">SFDR RTS Amendment Proposal</p>
                  <p className="text-sm text-blue-700 mt-1">EU Commission proposes amendments to SFDR regulatory technical standards.</p>
                  <p className="text-xs text-blue-600 mt-2">1 week ago</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Deadline Widget */}
          <DeadlineWidget maxItems={5} showHeader={true} daysAhead={90} />
        </div>
      )}

      {/* AI Disclosure Draft Modal */}
      <AIAssistantModal
        isOpen={showDisclosureModal}
        onClose={() => setShowDisclosureModal(false)}
        title={`Draft Disclosure: ${selectedGap?.id || ''}`}
        icon={FileText}
        iconColor="text-blue-600"
        iconBg="bg-blue-100"
        loading={disclosureLoading}
        result={disclosureResult}
        error={disclosureError}
        onRetry={() => selectedGap && generateDisclosureDraft(selectedGap)}
      />
    </div>
  );
};

export default ComplianceConsole;
